import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, IconButton, Stack, Typography, alpha, useTheme } from '@mui/material';
import { AddPhotoAlternate as PhotoIcon, Close as CloseIcon } from '@mui/icons-material';

const MAX_IMAGE_BYTES = 12 * 1024 * 1024; // shrunk in the browser before upload

/**
 * Photo drop zone / preview with replace + remove. `value` is
 * { file, url, removed, error }; the caller uploads `file` after saving.
 */
const MenuImagePicker = ({ value, onChange, disabled, t, height = 160, hint, fit = 'cover' }) => {
  const theme = useTheme();
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const objectUrl = useMemo(() => (value.file ? URL.createObjectURL(value.file) : null), [value.file]);
  useEffect(() => () => { if (objectUrl) URL.revokeObjectURL(objectUrl); }, [objectUrl]);
  const preview = objectUrl || (!value.removed && value.url) || null;

  const pick = (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) { onChange({ ...value, error: t('menus.imageInvalid') }); return; }
    if (file.size > MAX_IMAGE_BYTES) { onChange({ ...value, error: t('menus.imageTooLarge') }); return; }
    onChange({ ...value, file, removed: false, error: null });
  };

  return (
    <Box>
      <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/webp" hidden onChange={(e) => { pick(e.target.files?.[0]); e.target.value = ''; }} />
      {preview ? (
        <Box sx={{ position: 'relative', borderRadius: 2, overflow: 'hidden', height, bgcolor: 'action.hover' }}>
          <Box component="img" src={preview} alt="" sx={{ width: '100%', height: '100%', objectFit: fit, display: 'block' }} />
          {!disabled && (
            <Stack direction="row" spacing={1} sx={{ position: 'absolute', right: 8, bottom: 8 }}>
              <Button size="small" variant="contained" color="inherit" startIcon={<PhotoIcon />} onClick={() => inputRef.current?.click()} sx={{ bgcolor: 'rgba(255,255,255,0.92)', color: '#111' }}>
                {t('menus.imageReplace')}
              </Button>
              <IconButton size="small" onClick={() => onChange({ file: null, url: value.url, removed: true, error: null })} sx={{ bgcolor: 'rgba(255,255,255,0.92)', color: '#111' }} aria-label={t('menus.imageRemove')}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Stack>
          )}
        </Box>
      ) : (
        <Box
          role="button"
          tabIndex={0}
          onClick={() => !disabled && inputRef.current?.click()}
          onKeyDown={(e) => { if (!disabled && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); inputRef.current?.click(); } }}
          onDragOver={(e) => { e.preventDefault(); if (!disabled) setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => { e.preventDefault(); setDragging(false); if (!disabled) pick(e.dataTransfer.files?.[0]); }}
          sx={{
            height,
            borderRadius: 2,
            border: '2px dashed',
            borderColor: dragging ? 'primary.main' : alpha(theme.palette.primary.main, 0.35),
            bgcolor: dragging ? alpha(theme.palette.primary.main, 0.08) : alpha(theme.palette.primary.main, 0.04),
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.5,
            px: 2,
            textAlign: 'center',
            cursor: disabled ? 'default' : 'pointer',
            color: 'text.secondary',
            transition: 'all .15s',
          }}
        >
          <PhotoIcon color="primary" />
          <Typography variant="body2" fontWeight={600} color="text.primary">{t('menus.imageAdd')}</Typography>
          <Typography variant="caption">{hint || t('menus.imageHint')}</Typography>
        </Box>
      )}
      {value.error && <Typography variant="caption" color="error" display="block" mt={0.5}>{value.error}</Typography>}
    </Box>
  );
};

export const emptyImage = (url = null) => ({ file: null, url, removed: false, error: null });

export default MenuImagePicker;
